import React, { useState } from 'react';

import Button from 'components/partials/button/button';

import type { UseAdmitAdjustmentsResult } from '../hooks/use-admit-adjustments';

import CustomPopulationModal from './admit-adjustments/custom-population-modal';
import NeedBucketAccordion from './admit-adjustments/need-bucket-accordion';
import NewAdmitsBadge from './admit-adjustments/new-admits-badge';
import NotIncludedWarning from './admit-adjustments/not-included-warning';
import PopulationTabs from './admit-adjustments/population-tabs';
import RemoveCustomPopulationModal from './admit-adjustments/remove-custom-population-modal';

import Icons from 'assets/icons';

const CONTENT_OFFSET_PX = 630;

type PopulationModalState =
  | { mode: 'add' }
  | { mode: 'edit'; tabKey: string }
  | { mode: 'remove'; tabKey: string }
  | null;

interface AdmitAdjustmentsTabProps {
  admitAdjustments: UseAdmitAdjustmentsResult;
  clientId?: string | number;
  readOnly?: boolean;
}

const AdmitAdjustmentsTab = ({
  admitAdjustments,
  clientId,
  readOnly,
}: AdmitAdjustmentsTabProps): React.JSX.Element => {
  const [modal, setModal] = useState<PopulationModalState>(null);
  const {
    userId,
    modelId,
    tabs,
    activeTabKey,
    activeAwardType,
    setActiveTabKey,
    bucketGroups,
    notIncludedStudents,
    newAdmits,
    isDirtyGlobal,
    isSaving,
    handleAdmitMultiplierChange,
    handleYieldMultiplierChange,
    handleRowUndo,
    handleRevertAll,
    handleSaveDraft,
    handleAddPopulation,
    handleUpdatePopulation,
    handleRemovePopulation,
    handleReorderTabs,
  } = admitAdjustments;

  const closeModal = () => setModal(null);
  const editedTab =
    modal && modal.mode !== 'add'
      ? tabs.find((tab) => tab.key === modal.tabKey)
      : undefined;
  const editedPopulation =
    editedTab?.kind === 'custom' ? editedTab.population : undefined;

  return (
    <div
      className="flex flex-col gap-4"
      data-testid="results-admit-adjustments-tab"
    >
      <div className="flex items-center justify-between max-h-7">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-bold text-gray-700">Admit adjustments</h3>
          <NewAdmitsBadge newAdmits={newAdmits} />
        </div>
        <div className="flex items-center gap-5">
          {isDirtyGlobal && (
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="reserve-outlined"
                size="xs"
                iconBefore={<Icons.Undo aria-hidden="true" />}
                onClick={handleRevertAll}
                data-testid="admit-revert-changes"
              >
                Revert changes
              </Button>
              <Button
                type="button"
                variant="reserve-filled"
                size="xs"
                onClick={handleSaveDraft}
                loading={isSaving}
                data-testid="admit-apply-adjustments"
              >
                Apply adjustments
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <PopulationTabs
          tabs={tabs}
          activeTabKey={activeTabKey}
          readOnly={readOnly}
          onSelect={setActiveTabKey}
          onEdit={(key) => setModal({ mode: 'edit', tabKey: key })}
          onAdd={() => setModal({ mode: 'add' })}
          onReorder={handleReorderTabs}
        />
        <NotIncludedWarning students={notIncludedStudents} />
      </div>

      {!bucketGroups.length && (
        <div className="py-6 text-center text-gray-500 text-sm">
          No adjustment data available for this population.
        </div>
      )}

      {!!bucketGroups.length && (
        <div
          className="overflow-hidden"
          style={{
            height: `calc(100vh - ${CONTENT_OFFSET_PX}px)`,
            minHeight: '200px',
          }}
        >
          <div className="h-full overflow-y-auto scrollbar-thin space-y-3 pr-1">
            {bucketGroups.map((group, index) => (
              <NeedBucketAccordion
                key={`${activeTabKey}-${group.bucketRank}`}
                group={group}
                ownerKey={activeTabKey!}
                awardType={activeAwardType}
                defaultExpanded={index === 0}
                onAdmitMultiplierChange={handleAdmitMultiplierChange}
                onYieldMultiplierChange={handleYieldMultiplierChange}
                onRowUndo={handleRowUndo}
                readOnly={readOnly}
              />
            ))}
          </div>
        </div>
      )}

      {modal?.mode === 'add' && (
        <CustomPopulationModal
          userId={userId}
          modelId={modelId}
          clientId={clientId ?? ''}
          onSubmit={(definition) => {
            handleAddPopulation(definition);
            closeModal();
          }}
          onClose={closeModal}
        />
      )}

      {modal?.mode === 'edit' && editedPopulation && (
        <CustomPopulationModal
          userId={userId}
          modelId={modelId}
          clientId={clientId ?? ''}
          population={editedPopulation}
          onSubmit={(definition) => {
            handleUpdatePopulation(modal.tabKey, definition);
            closeModal();
          }}
          onRemove={() => setModal({ mode: 'remove', tabKey: modal.tabKey })}
          onClose={closeModal}
        />
      )}

      {modal?.mode === 'remove' && (
        <RemoveCustomPopulationModal
          onConfirm={() => {
            handleRemovePopulation(modal.tabKey);
            closeModal();
          }}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default AdmitAdjustmentsTab;
